import { useState } from "react";
import { X, CheckCircle, XCircle, TrendingDown, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { AuctionConfirmModal } from "./AuctionConfirmModal";

interface AuctionConfirmModalState {
  isOpen: boolean;
  title: string;
  description: string;
  onConfirm: () => void;
  confirmLabel?: string;
  variant?: "default" | "destructive";
  icon?: React.ReactNode;
}

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type BidAuditMeta = any; // Simplified for the modal

type BidRow = {
  id: string;
  amount: number;
  status: string;
  source: string;
  meta: BidAuditMeta | null;
  createdAt: string;
  bidderUserId: string;
  bidderName: string;
  bidderEmail: string | null;
  bidderPhoneNumber?: string | null;
  bidderOrganizationId: string;
  bidderOrgName: string;
  bidderOrgSlug?: string | null;
};

type AuctionOffer = {
  id: string; round: number; offeredBy: string; amount: number; notes: string | null;
  status: string; createdAt: string; createdByUserId: string | null;
};

type AuctionDetail = {
  id: string; referenceNumber: string; title: string; description: string | null; status: string;
  pickupAddress: string | null; pickupPostalCode: string | null; pickupCity: string | null;
  visibility: string; startingBid: number; reservePrice: number | null; buyNowPrice: number | null;
  minimumIncrement: number; currentBid: number; spread: number; spreadLocked?: boolean; bidCount: number;
  startsAt: string | null; endsAt: string | null; publishedAt: string | null;
  closedAt: string | null; submittedAt: string | null; createdAt: string; updatedAt: string;
  organization: { id: string; name: string; slug: string };
  vehicle: any | null;
  bids: BidRow[];
  offers: AuctionOffer[];
  orderLines: any[];
  documents: any[];
  supportTickets: any[];
};

interface NegotiationModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: AuctionDetail;
  isDeveloper: boolean;
  onUpdate: (data: Partial<AuctionDetail>) => void;
  loadAuction: () => Promise<void>;
}

export function NegotiationModal({ isOpen, onClose, data, isDeveloper, onUpdate, loadAuction }: NegotiationModalProps) {
  const [spreadOverride, setSpreadOverride] = useState("");
  const [savingSpread, setSavingSpread] = useState(false);
  const [offerForm, setOfferForm] = useState({ offeredBy: "seller" as "seller" | "buyer", amount: "", notes: "" });
  const [submittingOffer, setSubmittingOffer] = useState(false);
  const [confirmModal, setConfirmModal] = useState<AuctionConfirmModalState>({
    isOpen: false,
    title: "",
    description: "",
    onConfirm: () => {},
  });

  const closeConfirm = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  const isPendingSellerDecision = data.status === "pending_seller_decision";
  const isNoBids = data.status === "no_bids";
  const isNegotiating = data.status === "negotiating";

  async function post(path: string, body?: object) {
    const r = await fetch(`${API}/api/admin/auctions/${data.id}${path}`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: body ? JSON.stringify(body) : undefined,
    });
    return r.ok ? r.json() : null;
  }

  async function patch(path: string, body: object) {
    const r = await fetch(`${API}/api/admin/auctions/${data.id}${path}`, {
      method: "PATCH", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return r.ok ? r.json() : null;
  }

  if (!isOpen) return null;

  function formatDateTime(iso: string) {
    return new Date(iso).toLocaleString("nb-NO", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit"
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
      <Card className="w-full max-w-2xl shadow-2xl animate-in fade-in zoom-in duration-200 my-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold">Forhandling</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{data.title}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
          {!(isPendingSellerDecision || isNoBids || isNegotiating) ? (
            <div className="flex flex-col items-center justify-center py-10 text-muted-foreground gap-3">
              <TrendingDown className="h-10 w-10 opacity-20" />
              <p className="text-sm">Forhandlingsmodulen er kun aktiv når auksjonen venter på beslutning.</p>
              <p className="text-xs">Nåværende status: <span className="font-mono">{data.status}</span></p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Situation */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Kjøperbud</p>
                  <p className="text-lg font-bold text-green-600">
                    {data.currentBid > 0 ? `${data.currentBid.toLocaleString("nb-NO")} kr` : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Spread</p>
                  <p className="text-lg font-bold text-blue-600">{data.spread.toLocaleString("nb-NO")} kr</p>
                  {data.spreadLocked && (
                    <span className="mt-1 inline-block rounded bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">Overstyrt</span>
                  )}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Selgerbud</p>
                  <p className="text-lg font-bold text-amber-600">
                    {data.currentBid > 0 ? `${(data.currentBid - data.spread).toLocaleString("nb-NO")} kr` : "—"}
                  </p>
                </div>
                {data.reservePrice && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Minstepris</p>
                    <p className="text-lg font-bold">{data.reservePrice.toLocaleString("nb-NO")} kr</p>
                  </div>
                )}
                {data.reservePrice && data.currentBid > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Gap (admin)</p>
                    <p className={`text-lg font-bold ${data.reservePrice - data.currentBid > 0 ? "text-destructive" : "text-green-600"}`}>
                      {data.reservePrice - data.currentBid > 0
                        ? `−${(data.reservePrice - data.currentBid).toLocaleString("nb-NO")} kr`
                        : "Dekket"}
                    </p>
                  </div>
                )}
              </div>

              {isDeveloper && (
                <div className="pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-2">Overstyr spread</p>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      className="flex-1"
                      placeholder={`${data.spread.toLocaleString("nb-NO")} (nåværende)`}
                      value={spreadOverride}
                      onChange={e => setSpreadOverride(e.target.value)}
                      min={0}
                    />
                    <Button
                      size="sm"
                      disabled={savingSpread || spreadOverride === ""}
                      onClick={async () => {
                        const val = parseInt(spreadOverride, 10);
                        if (isNaN(val) || val < 0) return;
                        setSavingSpread(true);
                        const r = await fetch(`${API}/api/admin/auctions/${data.id}/spread`, {
                          method: "PATCH", credentials: "include",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ spread: val }),
                        });
                        if (r.ok) {
                          onUpdate({ spread: val, spreadLocked: true });
                          setSpreadOverride("");
                        }
                        setSavingSpread(false);
                      }}
                    >
                      {savingSpread ? "Lagrer..." : "Lagre"}
                    </Button>
                  </div>
                </div>
              )}

              {/* Decision */}
              <div className="pt-4 border-t border-border space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Beslutning</p>
                <div className="grid grid-cols-2 gap-3">
                  <Button className="gap-2" onClick={() => {
                    setConfirmModal({
                      isOpen: true,
                      title: "Aksepter salg",
                      description: "Bekreft at selger aksepterer høyeste bud og auksjonen lukkes?",
                      icon: <CheckCircle className="h-8 w-8 text-green-600" />,
                      confirmLabel: "Aksepter",
                      onConfirm: async () => {
                        closeConfirm();
                        const r = await post("/accept");
                        if (r) onUpdate({ status: r.status });
                      }
                    });
                  }}>
                    <CheckCircle className="h-4 w-4" /> Aksepter salg
                  </Button>
                  <Button variant="outline" className="gap-2 text-destructive border-destructive/40 hover:bg-destructive/5" onClick={() => {
                    setConfirmModal({
                      isOpen: true,
                      variant: "destructive",
                      title: "Avslå salg",
                      description: "Bekreft at selger avslår — auksjonen avsluttes uten handel?",
                      icon: <XCircle className="h-8 w-8 text-destructive" />,
                      confirmLabel: "Avslå",
                      onConfirm: async () => {
                        closeConfirm();
                        const r = await post("/reject");
                        if (r) onUpdate({ status: r.status });
                      }
                    });
                  }}>
                    <XCircle className="h-4 w-4" /> Avslå
                  </Button>
                </div>
              </div>

              {/* History */}
              <div className="pt-4 border-t border-border space-y-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Forhandlingshistorikk</p>
                {data.offers.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">Ingen motbud registrert ennå.</p>
                ) : (
                  <div className="space-y-3">
                    {data.offers.map((offer) => {
                      const isSeller = offer.offeredBy === "seller";
                      return (
                        <div key={offer.id} className="rounded-xl border border-border bg-muted/20 p-3">
                          <div className="flex justify-between items-start gap-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge variant={isSeller ? "warning" : "accent"} className="px-1.5 py-0 text-[10px] h-4">
                                  {isSeller ? "Fra selger" : "Fra kjøper"}
                                </Badge>
                                <span className="text-sm font-bold">{offer.amount.toLocaleString("nb-NO")} kr</span>
                              </div>
                              <p className="text-[10px] text-muted-foreground mt-1">{formatDateTime(offer.createdAt)}</p>
                            </div>
                            <div className="flex flex-col items-end gap-1.5">
                              {offer.status === "pending" ? (
                                <div className="flex gap-1.5">
                                  <Button size="icon" variant="outline" className="h-6 w-6 text-green-600" onClick={() => {
                                    setConfirmModal({
                                      isOpen: true,
                                      title: "Godta bud",
                                      description: "Marker dette budet som akseptert?",
                                      icon: <CheckCircle className="h-8 w-8 text-green-600" />,
                                      confirmLabel: "Godta",
                                      onConfirm: async () => {
                                        closeConfirm();
                                        const r = await patch(`/offers/${offer.id}/accept`, {});
                                        if (r) void loadAuction();
                                      }
                                    });
                                  }}>
                                    <CheckCircle className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button size="icon" variant="outline" className="h-6 w-6 text-destructive" onClick={async () => {
                                    const r = await patch(`/offers/${offer.id}/reject`, {});
                                    if (r) onUpdate({ offers: data.offers.map(o => o.id === offer.id ? { ...o, status: "rejected" } : o) });
                                  }}>
                                    <XCircle className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              ) : (
                                <Badge variant={offer.status === "accepted" ? "success" : "destructive"} className="text-[10px] py-0">
                                  {offer.status === "accepted" ? "Akseptert" : "Avslått"}
                                </Badge>
                              )}
                            </div>
                          </div>
                          {offer.notes && <p className="mt-2 text-xs text-muted-foreground italic border-t border-border/50 pt-2">{offer.notes}</p>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Log new */}
              <div className="pt-4 border-t border-border space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Logg nytt motbud</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] text-muted-foreground uppercase">Fra</Label>
                    <select
                      className="w-full rounded-lg border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      value={offerForm.offeredBy}
                      onChange={e => setOfferForm(p => ({ ...p, offeredBy: e.target.value as "seller" | "buyer" }))}
                    >
                      <option value="seller">Selger</option>
                      <option value="buyer">Kjøper</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] text-muted-foreground uppercase">Beløp (kr)</Label>
                    <Input
                      type="number"
                      value={offerForm.amount}
                      onChange={e => setOfferForm(p => ({ ...p, amount: e.target.value }))}
                      placeholder="0"
                      className="h-9"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] text-muted-foreground uppercase">Notat</Label>
                  <Input
                    value={offerForm.notes}
                    onChange={e => setOfferForm(p => ({ ...p, notes: e.target.value }))}
                    placeholder="Valgfri kommentar..."
                    className="h-9"
                  />
                </div>
                <Button
                  className="w-full"
                  disabled={submittingOffer || !offerForm.amount}
                  onClick={async () => {
                    setSubmittingOffer(true);
                    const r = await post("/offers", {
                      offeredBy: offerForm.offeredBy,
                      amount: Number(offerForm.amount),
                      notes: offerForm.notes || undefined,
                    });
                    setSubmittingOffer(false);
                    if (r) {
                      onUpdate({ offers: [...data.offers, r] });
                      setOfferForm(p => ({ ...p, amount: "", notes: "" }));
                    }
                  }}
                >
                  {submittingOffer ? "Logger..." : "Logg motbud"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      <AuctionConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        description={confirmModal.description}
        confirmLabel={confirmModal.confirmLabel}
        variant={confirmModal.variant}
        icon={confirmModal.icon}
        isLoading={savingSpread || submittingOffer}
      />
    </div>
  );
}
